// src/pages/DeviceList.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fmt, PRICES } from "../config/devices";
import { CampusBadge, StatusBadge } from "../components/UI";

const REVIEW_STORE_PREFIX = "cwc_budget_review_v1_";

function reviewStoreKey(program, year) {
  return program === "5-year"
    ? REVIEW_STORE_PREFIX + "5-year_" + year
    : REVIEW_STORE_PREFIX + year;
}

const REVIEW_OPTIONS = [
  { value: "unreviewed", label: "Unreviewed" },
  { value: "verified", label: "Verified" },
  { value: "needs-jira-update", label: "Needs Jira Update" },
  { value: "add-to-budget", label: "Add To Budget" },
  { value: "excluded", label: "Exclude From Budget" },
];

const ADD_TO_BUDGET = "add-to-budget";

function csvCell(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
}

function defaultReviewState() {
  return { devices: {}, miscItems: [], finalizedAt: null, finalizedBy: null, finalizedCampuses: {}, jiraKey: null, jiraTickets: [], lastSavedAt: null, lastSavedBy: null };
}

function sortValue(device, col) {
  if (["eolYear"].includes(col)) return Number(device[col]) || 0;
  if (["dateOrdered", "warrantyDate", "defenderLastLogon"].includes(col)) {
    const parsed = Date.parse(device[col] || "");
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return String(device[col] ?? "").toLowerCase();
}

export default function DeviceList({ computeDevices, allDevices, jiraMap, defenderMap, priceMap, devicePrice, reviewer, onDirtyChange, lifecycleYears = 4, reviewProgram = "4-year", readOnly = false }) {
  const prices = priceMap || PRICES;
  const [search, setSearch]             = useState("");
  const [campusFilter, setCampusFilter] = useState("All");
  const [yearFilter, setYearFilter]     = useState("");
  const [sortCol, setSortCol]           = useState("eolYear");
  const [sortDir, setSortDir]           = useState("asc");
  const [showAll, setShowAll]           = useState(false);
  const [reviewMode, setReviewMode]     = useState(false);
  const [futurePickerOpen, setFuturePickerOpen] = useState(false);
  const [futureSearch, setFutureSearch] = useState("");
  const [reviewYear, setReviewYear]     = useState(new Date().getFullYear());
  const [reviewState, setReviewState]   = useState(defaultReviewState);
  const [reviewLoaded, setReviewLoaded] = useState(false);
  const [reviewSyncStatus, setReviewSyncStatus] = useState("");
  const [manualSaveStatus, setManualSaveStatus] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [finalizing, setFinalizing]     = useState(false);
  const [finalizeError, setFinalizeError] = useState("");
  const [reviewStartError, setReviewStartError] = useState("");
  const [reviewBusyMessage, setReviewBusyMessage] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [conflictError, setConflictError] = useState(null);
  const reviewVersionRef = useRef(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const currentReviewer = reviewer || { name: "Unknown reviewer", email: "" };

  const hasJira     = Object.keys(jiraMap).length > 0;
  const hasDefender = Object.keys(defenderMap || {}).length > 0;
  const currentYear = new Date().getFullYear();

  const campuses = useMemo(() => ["All", ...Array.from(new Set(computeDevices.map(d => d.campus).filter(Boolean))).sort()], [computeDevices]);
  const years = useMemo(() => Array.from(new Set(computeDevices.map(d => Number(d.eolYear)))).filter(Boolean).sort((a, b) => a - b), [computeDevices]);

  const base = showAll ? allDevices.filter(d => d.eolYear) : computeDevices;

  const saveReviewState = useCallback(async (state, successLabel = "Review saved", applyMerged = false) => {
    try {
      localStorage.setItem(reviewStoreKey(reviewProgram, reviewYear), JSON.stringify(state));
    } catch { /* ignore local storage failures */ }

    try {
      const res = await fetch("/api/budget-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year: reviewYear, program: reviewProgram, version: reviewVersionRef.current, state }),
      });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        setConflictError(body.message || "Someone else saved changes while you were editing. Reload the latest version before saving.");
        setReviewSyncStatus("Save rejected — version conflict");
        return false;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json().catch(() => ({}));
      if (typeof body.version === "number") reviewVersionRef.current = body.version;
      if (applyMerged && body.state) {
        setReviewState({ ...defaultReviewState(), ...body.state });
        try {
          localStorage.setItem(reviewStoreKey(reviewProgram, reviewYear), JSON.stringify(body.state));
        } catch { /* ignore local storage failures */ }
      }
      setIsDirty(false);
      setConflictError(null);
      setReviewSyncStatus(`${successLabel} · ${new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`);
      return true;
    } catch {
      setReviewSyncStatus("Local changes saved; shared save failed");
      return false;
    }
  }, [reviewYear, reviewProgram]);

  const loadSharedReview = useCallback(async (statusLabel = "Shared review loaded") => {
    const res = await fetch(`/api/budget-review?year=${encodeURIComponent(reviewYear)}&program=${encodeURIComponent(reviewProgram)}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    reviewVersionRef.current = typeof data.version === "number" ? data.version : 0;
    const next = { ...defaultReviewState(), ...(data.state || {}) };
    setReviewState(next);
    setIsDirty(false);
    setConflictError(null);
    try {
      localStorage.setItem(reviewStoreKey(reviewProgram, reviewYear), JSON.stringify(next));
    } catch { /* ignore local storage failures */ }
    setReviewSyncStatus(data.updatedAt
      ? `${statusLabel} · ${new Date(data.updatedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`
      : statusLabel
    );
    setManualSaveStatus("");
    return data;
  }, [reviewYear, reviewProgram]);

  useEffect(() => {
    let cancelled = false;
    setReviewLoaded(false);
    setReviewSyncStatus("Loading shared review…");

    const localState = (() => {
      try {
        const stored = localStorage.getItem(reviewStoreKey(reviewProgram, reviewYear));
        return stored ? { ...defaultReviewState(), ...JSON.parse(stored) } : defaultReviewState();
      } catch {
        return defaultReviewState();
      }
    })();
    setReviewState(localState);

    loadSharedReview("Shared review loaded")
      .catch(() => {
        if (cancelled) return;
        setReviewSyncStatus("Using local review copy");
      })
      .finally(() => {
        if (!cancelled) setReviewLoaded(true);
      });

    return () => { cancelled = true; };
  }, [reviewYear, loadSharedReview]);

  // Propagate dirty state to parent (App.js) for nav-switch guard
  useEffect(() => { onDirtyChange?.(isDirty && reviewMode); }, [isDirty, reviewMode, onDirtyChange]);

  // Warn on browser close/refresh when dirty
  useEffect(() => {
    if (!isDirty || !reviewMode) return;
    const handler = e => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty, reviewMode]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return base
      .filter(d =>
        (campusFilter === "All" || d.campus === campusFilter) &&
        (!yearFilter || Number(d.eolYear) === Number(yearFilter)) &&
        (!s || [
          d.serial, d.jiraDescription, d.deviceType, d.campus, d.description,
          d.orderNo, d.invoiceId, d.assignedTo, d.defenderLastUser,
          d.defenderLastDisplayName, d.hostname
        ].some(v => v != null && String(v).toLowerCase().includes(s)))
      )
      .sort((a, b) => {
        const av = sortValue(a, sortCol), bv = sortValue(b, sortCol);
        const result = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? result : -result;
      });
  }, [base, campusFilter, yearFilter, search, sortCol, sortDir]);

  const matchesSearch = useCallback((d, term) => (
    !term || [
      d.serial, d.jiraDescription, d.deviceType, d.campus, d.description,
      d.orderNo, d.invoiceId, d.assignedTo, d.defenderLastUser,
      d.defenderLastDisplayName, d.hostname, d.jiraStatus, d.jiraKey
    ].some(v => v != null && String(v).toLowerCase().includes(term))
  ), []);

  const reviewScopeDevices = useMemo(() => computeDevices.filter(d => (
    Number(d.eolYear) === Number(reviewYear) &&
    (campusFilter === "All" || d.campus === campusFilter)
  )), [computeDevices, reviewYear, campusFilter]);

  const reviewAddedDevices = useMemo(() => computeDevices.filter(d => (
    (reviewState.devices[d.serial]?.status || "unreviewed") === ADD_TO_BUDGET &&
    Number(d.eolYear) !== Number(reviewYear) &&
    (campusFilter === "All" || d.campus === campusFilter)
  )), [computeDevices, reviewState.devices, reviewYear, campusFilter]);

  const futureCandidateDevices = useMemo(() => {
    const s = futureSearch.trim().toLowerCase();
    return computeDevices
      .filter(d => {
        if (campusFilter !== "All" && d.campus !== campusFilter) return false;
        if (Number(d.eolYear) <= Number(reviewYear)) return false;
        if (reviewState.devices[d.serial]?.status === ADD_TO_BUDGET) return false;
        return matchesSearch(d, s);
      })
      .sort((a, b) => {
        const av = sortValue(a, sortCol), bv = sortValue(b, sortCol);
        const result = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? result : -result;
      });
  }, [computeDevices, campusFilter, futureSearch, matchesSearch, reviewState.devices, reviewYear, sortCol, sortDir]);

  const reviewDevices = useMemo(() => {
    const sortDevices = devices => [...devices].sort((a, b) => {
        const av = sortValue(a, sortCol), bv = sortValue(b, sortCol);
        const result = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? result : -result;
      });
    const sortedBudgetDevices = sortDevices([...reviewScopeDevices, ...reviewAddedDevices]);
    const s = search.trim().toLowerCase();
    return s
      ? sortedBudgetDevices.filter(d => matchesSearch(d, s))
      : sortedBudgetDevices;
  }, [reviewScopeDevices, reviewAddedDevices, search, matchesSearch, sortCol, sortDir]);

  const reviewBudgetDevices = useMemo(() => [...reviewScopeDevices, ...reviewAddedDevices].filter(d => (
    Number(d.eolYear) === Number(reviewYear) ||
    (reviewState.devices[d.serial]?.status || "unreviewed") === ADD_TO_BUDGET
  )), [reviewScopeDevices, reviewAddedDevices, reviewState.devices, reviewYear]);

  const reviewMiscItems = useMemo(() => (reviewState.miscItems || []).filter(item =>
    !item.removed &&
    Number(item.year) === Number(reviewYear) &&
    (campusFilter === "All" || item.campus === campusFilter)
  ), [reviewState.miscItems, reviewYear, campusFilter]);

  const reviewSummary = useMemo(() => {
    const counts = { unreviewed: 0, verified: 0, needsJiraUpdate: 0, addToBudget: 0, excluded: 0 };
    reviewBudgetDevices.forEach(d => {
      const status = reviewState.devices[d.serial]?.status || "unreviewed";
      if (status === "verified") counts.verified += 1;
      else if (status === "needs-jira-update") counts.needsJiraUpdate += 1;
      else if (status === ADD_TO_BUDGET) counts.addToBudget += 1;
      else if (status === "excluded") counts.excluded += 1;
      else counts.unreviewed += 1;
    });
    const included = reviewBudgetDevices.filter(d => (reviewState.devices[d.serial]?.status || "unreviewed") !== "excluded");
    const miscBudget = reviewMiscItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    return {
      ...counts,
      total: reviewBudgetDevices.length + reviewMiscItems.length,
      included: included.length + reviewMiscItems.length,
      budget: included.reduce((sum, d) => sum + (prices[d.deviceType] || 0), 0) + miscBudget,
      cck: included.filter(d => d.campus === "CCK").length,
      ccw: included.filter(d => d.campus === "CCW").length,
      misc: reviewMiscItems.length,
    };
  }, [reviewBudgetDevices, reviewMiscItems, reviewState.devices, prices]);

  const currentCampusFinalization = useMemo(() => {
    if (!campusFilter || campusFilter === "All") return null;
    const direct = reviewState.finalizedCampuses?.[campusFilter];
    if (direct?.finalizedAt) return direct;

    const legacyTicket = (reviewState.jiraTickets || []).find(t => t.campus === campusFilter);
    if (reviewState.finalizedAt && legacyTicket) {
      return {
        finalizedAt: reviewState.finalizedAt,
        finalizedBy: reviewState.finalizedBy,
        jiraKey: legacyTicket.key,
        jiraTickets: [legacyTicket],
      };
    }
    return null;
  }, [reviewState.finalizedCampuses, reviewState.finalizedAt, reviewState.finalizedBy, reviewState.jiraTickets, campusFilter]);

  const handleSort = col => {
    setSortDir(d => sortCol === col && d === "asc" ? "desc" : "asc");
    setSortCol(col);
  };

  const reviewerStamp = () => ({
    name: currentReviewer.name,
    email: currentReviewer.email,
  });

  const clearCampusFinalization = (state, campus) => {
    const finalizedCampuses = { ...(state.finalizedCampuses || {}) };
    if (campus && campus !== "All") delete finalizedCampuses[campus];
    return {
      ...state,
      finalizedAt: Object.keys(finalizedCampuses).length ? state.finalizedAt : null,
      finalizedBy: Object.keys(finalizedCampuses).length ? state.finalizedBy : null,
      finalizedCampuses,
      jiraKey: Object.keys(finalizedCampuses).length ? state.jiraKey : null,
      jiraTickets: Object.keys(finalizedCampuses).length ? state.jiraTickets : [],
    };
  };

  const startBudgetReview = async () => {
    if (readOnly) return;
    if (campusFilter === "All" || !yearFilter) {
      const message = "Choose one campus and one year before starting Budget Review.";
      setReviewStartError(message);
      window.alert(message);
      return;
    }

    const year = Number(yearFilter);
    setReviewStartError("");
    setReviewYear(year);
    setYearFilter(String(year));
    setShowAll(false);
    setFinalizeError("");
    setManualSaveStatus("");
    setReviewLoaded(false);
    setReviewSyncStatus("Loading shared review…");
    setReviewBusyMessage("Loading shared review…");
    try {
      const res = await fetch(`/api/budget-review?year=${encodeURIComponent(year)}&program=${encodeURIComponent(reviewProgram)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      reviewVersionRef.current = typeof data.version === "number" ? data.version : 0;
      const next = { ...defaultReviewState(), ...(data.state || {}) };
      setReviewState(next);
      setIsDirty(false);
      setConflictError(null);
      try {
        localStorage.setItem(reviewStoreKey(reviewProgram, year), JSON.stringify(next));
      } catch { /* ignore local storage failures */ }
      setReviewSyncStatus(data.updatedAt
        ? `Shared review loaded · ${new Date(data.updatedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}`
        : "Shared review loaded"
      );
    } catch {
      setReviewSyncStatus("Using local review copy");
    } finally {
      setReviewLoaded(true);
      setReviewMode(true);
      setReviewBusyMessage("");
    }
  };

  const setReview = (serial, patch) => {
    setIsDirty(true);
    setReviewState(prev => {
      const now = new Date().toISOString();
      const stamp = reviewerStamp();
      const previous = prev.devices[serial] || { status: "unreviewed", note: "" };
      const baseState = clearCampusFinalization(prev, campusFilter);
      return {
        ...baseState,
        lastSavedAt: now,
        lastSavedBy: stamp,
        devices: {
          ...baseState.devices,
          [serial]: {
            ...previous,
            ...patch,
            reviewedAt: now,
            reviewedBy: stamp,
            activity: [
              ...(previous.activity || []).slice(-9),
              { at: now, by: stamp, fields: Object.keys(patch) },
            ],
          },
        },
      };
    });
  };

  const removeAddedBudgetDevice = serial => {
    setIsDirty(true);
    setReviewState(prev => {
      const now = new Date().toISOString();
      const stamp = reviewerStamp();
      const previous = prev.devices[serial] || { status: "unreviewed", note: "" };
      const baseState = clearCampusFinalization(prev, campusFilter);
      return {
        ...baseState,
        lastSavedAt: now,
        lastSavedBy: stamp,
        devices: {
          ...baseState.devices,
          [serial]: {
            ...previous,
            status: "unreviewed",
            reviewedAt: now,
            reviewedBy: stamp,
            activity: [
              ...(previous.activity || []).slice(-9),
              { at: now, by: stamp, fields: ["status"], action: "removed-add-to-budget" },
            ],
          },
        },
      };
    });
  };

  const addMiscLineItem = () => {
    setIsDirty(true);
    if (!reviewMode || campusFilter === "All") {
      window.alert("Choose one campus before adding a miscellaneous budget item.");
      return;
    }
    const description = window.prompt("Description for this budget item?");
    if (!description?.trim()) return;
    const amount = Math.round(Number(devicePrice ?? 1704));
    const note = window.prompt("Reason / note for this addition?", "") || "";
    setReviewState(prev => {
      const now = new Date().toISOString();
      const stamp = reviewerStamp();
      const baseState = clearCampusFinalization(prev, campusFilter);
      return {
        ...baseState,
        lastSavedAt: now,
        lastSavedBy: stamp,
        miscItems: [
          ...(baseState.miscItems || []),
          {
            id: `misc-${Date.now()}`,
            year: reviewYear,
            campus: campusFilter,
            description: description.trim(),
            amount,
            note: note.trim(),
            addedAt: now,
            updatedAt: now,
            addedBy: stamp,
          },
        ],
      };
    });
  };

  const removeMiscLineItem = id => {
    if (!window.confirm("Remove this miscellaneous budget item?")) return;
    setIsDirty(true);
    setReviewState(prev => {
      const now = new Date().toISOString();
      const stamp = reviewerStamp();
      const baseState = clearCampusFinalization(prev, campusFilter);
      return {
        ...baseState,
        lastSavedAt: now,
        lastSavedBy: stamp,
        miscItems: (baseState.miscItems || []).map(item => item.id === id
          ? { ...item, removed: true, removedAt: now, removedBy: stamp, updatedAt: now }
          : item),
      };
    });
  };

  const applyStatusChange = (serial, nextStatus) => {
    setStatusMenu(null);
    setManualSaveStatus("");
    setConflictError(null);
    setIsDirty(true);
    setReviewState(prev => {
      const now = new Date().toISOString();
      const stamp = reviewerStamp();
      const previous = prev.devices?.[serial] || { status: "unreviewed", note: "" };
      const baseState = clearCampusFinalization(prev, campusFilter);
      return {
        ...baseState,
        lastSavedAt: now,
        lastSavedBy: stamp,
        devices: {
          ...(baseState.devices || {}),
          [serial]: {
            ...previous,
            status: nextStatus,
            reviewedAt: now,
            reviewedBy: stamp,
            activity: [
              ...(previous.activity || []).slice(-9),
              { at: now, by: stamp, fields: ["status"] },
            ],
          },
        },
      };
    });
    setReviewSyncStatus("Unsaved local changes");
    if (nextStatus === ADD_TO_BUDGET) setSearch("");
  };

  const addFutureBudgetDevice = serial => {
    applyStatusChange(serial, ADD_TO_BUDGET);
    setFutureSearch("");
    setFuturePickerOpen(false);
  };

  const exitReview = () => {
    if (isDirty && !window.confirm("You have unsaved budget review changes. Exit without saving?")) return;
    setReviewMode(false);
    setIsDirty(false);
    setConflictError(null);
    setManualSaveStatus("");
  };

  const reloadSharedReview = async () => {
    if (isDirty && !window.confirm("Reloading the shared review will discard your unsaved local changes. Continue?")) return;
    setReviewBusyMessage("Reloading shared review…");
    setReviewSyncStatus("Reloading shared review…");
    try {
      await loadSharedReview("Shared review reloaded");
    } catch {
      setReviewSyncStatus("Shared review reload failed");
    } finally {
      setReviewBusyMessage("");
    }
  };

  const saveCurrentReview = async () => {
    setManualSaving(true);
    setManualSaveStatus("Saving review…");
    const stamp = reviewerStamp();
    const next = {
      ...reviewState,
      lastSavedAt: new Date().toISOString(),
      lastSavedBy: stamp,
    };
    setReviewState(next);
    const saved = await saveReviewState(next, "Review saved", true);
    const time = new Date().toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" });
    setManualSaveStatus(saved
      ? `Review saved at ${time} by ${stamp.name}.`
      : `Review saved locally at ${time}, but shared save failed.`
    );
    setManualSaving(false);
  };

  const makeRows = (devices, includeReview = false) => devices.map(d => {
    const review = reviewState.devices[d.serial] || { status: "unreviewed", note: "" };
    const statusLabel = REVIEW_OPTIONS.find(o => o.value === review.status)?.label || "Unreviewed";
    return [
      d.serial, d.deviceType, d.campus, d.dateOrdered, d.warrantyDate,
      d.orderNo, d.invoiceId, d.eolYear, prices[d.deviceType] || "",
      d.assignedTo || "", d.jiraKey || "", d.jiraStatus || "",
      d.defenderLastUser || d.defenderLastDisplayName || "", formatDate(d.defenderLastLogon),
      ...(includeReview ? [statusLabel, review.note || "", review.reviewedBy?.name || "", review.reviewedAt || ""] : []),
    ];
  });

  const makeMiscRows = (items, includeReview = false) => items.map(item => [
    item.description,
    "Miscellaneous",
    item.campus,
    "",
    "",
    "",
    "",
    item.year,
    item.amount || "",
    "",
    "",
    "",
    "",
    "",
    ...(includeReview ? ["Add To Budget", item.note || "", item.addedBy?.name || "", item.addedAt || ""] : []),
  ]);

  const downloadCSV = (filename, headers, rows) => {
    const csv = [headers.join(","), ...rows.map(row => row.map(csvCell).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCSV = () => {
    const headers = ["Serial","Device_Type","Campus","Date_Ordered","Warranty_End","Order_No","Invoice_ID","EOL_Year","Est_Cost_AUD","Assigned_To","Jira_Key","Jira_Status","Last_Login_Azure","Last_Seen"];
    downloadCSV(`devices-eol-${yearFilter || "all"}-${campusFilter}.csv`, headers, makeRows(filtered));
  };

  const exportReviewCSV = () => {
    const headers = ["Serial","Device_Type","Campus","Date_Ordered","Warranty_End","Order_No","Invoice_ID","EOL_Year","Est_Cost_AUD","Assigned_To","Jira_Key","Jira_Status","Last_Login_Azure","Last_Seen","Review_Status","Review_Notes","Reviewed_By","Reviewed_At"];
    downloadCSV(`budget-review-${reviewYear}-${campusFilter}.csv`, headers, [
      ...makeRows(reviewBudgetDevices, true),
      ...makeMiscRows(reviewMiscItems, true),
    ]);
  };

  const finalizeReview = async () => {
    setFinalizeError("");
    if (reviewSummary.total === 0) {
      setFinalizeError(`No ${reviewYear} budget devices are available to finalise.`);
      return;
    }
    if (reviewSummary.unreviewed > 0) {
      setFinalizeError(`${reviewSummary.unreviewed} devices are still unreviewed.`);
      return;
    }
    const addToBudgetWithoutNotes = reviewBudgetDevices.filter(d => {
      const review = reviewState.devices[d.serial];
      return review?.status === ADD_TO_BUDGET && !String(review.note || "").trim();
    });
    if (addToBudgetWithoutNotes.length > 0) {
      setFinalizeError(`${addToBudgetWithoutNotes.length} manually added device${addToBudgetWithoutNotes.length === 1 ? " needs" : "s need"} a review note.`);
      return;
    }
    if (currentCampusFinalization?.finalizedAt && !window.confirm(`${campusFilter} ${reviewYear} was already finalised. Create a new Jira ticket and re-finalise this review?`)) {
      return;
    }

    setFinalizing(true);
    try {
      const stamp = reviewerStamp();
      const preflightAt = new Date().toISOString();
      const preflightState = {
        ...reviewState,
        lastSavedAt: preflightAt,
        lastSavedBy: stamp,
      };
      setReviewState(preflightState);
      const claimedVersion = await saveReviewState(preflightState, "Review checked before finalising", true);
      if (!claimedVersion) {
        throw new Error("The shared review changed before finalisation. Reload the latest review and try again; no Jira ticket was created.");
      }

      const rows = reviewBudgetDevices.map(d => {
        const review = reviewState.devices[d.serial] || { status: "unreviewed", note: "" };
        return {
          serial: d.serial,
          deviceType: d.deviceType,
          campus: d.campus,
          eolYear: d.eolYear,
          estimatedCost: prices[d.deviceType] || 0,
          assignedTo: d.assignedTo || "",
          jiraKey: d.jiraKey || "",
          jiraStatus: d.jiraStatus || "",
          lastSeen: d.defenderLastLogon || "",
          reviewStatus: review.status,
          reviewNote: review.note || "",
          reviewedBy: review.reviewedBy?.name || "",
          reviewedAt: review.reviewedAt || "",
        };
      });
      const miscRows = reviewMiscItems.map(item => ({
        serial: item.description,
        deviceType: "Miscellaneous",
        campus: item.campus,
        eolYear: item.year,
        estimatedCost: Number(item.amount) || 0,
        assignedTo: "",
        jiraKey: "",
        jiraStatus: "",
        lastSeen: "",
        reviewStatus: ADD_TO_BUDGET,
        reviewNote: item.note || "",
        reviewedBy: item.addedBy?.name || "",
        reviewedAt: item.addedAt || "",
      }));
      const res = await fetch("/api/jira-budget-review", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ year: reviewYear, program: reviewProgram, lifecycleYears, summary: reviewSummary, devices: [...rows, ...miscRows], reviewer: currentReviewer }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Jira ticket failed with HTTP ${res.status}`);

      const jiraTickets = body.tickets || [];
      const jiraKey = jiraTickets.map(t => t.key).filter(Boolean).join(", ") || body.key || body.issueKey || null;
      const finalizedAt = new Date().toISOString();
      const campusFinalization = campusFilter && campusFilter !== "All"
        ? {
            ...(preflightState.finalizedCampuses || {}),
            [campusFilter]: {
              finalizedAt,
              finalizedBy: stamp,
              jiraKey,
              jiraTickets,
              included: reviewSummary.included,
              budget: reviewSummary.budget,
            },
          }
        : (preflightState.finalizedCampuses || {});
      const finalState = {
        ...preflightState,
        finalizedAt,
        finalizedBy: stamp,
        finalizedCampuses: campusFinalization,
        jiraKey,
        jiraTickets,
        lastSavedAt: finalizedAt,
        lastSavedBy: stamp,
      };
      setReviewState(finalState);
      const saved = await saveReviewState(finalState, "Review finalised", true);
      if (!saved) throw new Error(`Jira ${jiraKey || "ticket"} was created, but the shared finalised state failed to save.`);
      exportReviewCSV();
    } catch (e) {
      setFinalizeError(String(e.message || e));
    } finally {
      setFinalizing(false);
    }
  };

  const Th = ({ col, label, align }) => (
    <th onClick={() => handleSort(col)} style={{ padding: "10px 14px", fontSize: 10, color: sortCol === col ? "var(--accent)" : "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: align || "left", cursor: "pointer", whiteSpace: "nowrap", userSelect: "none" }}>
      {label}{sortCol === col ? (sortDir === "asc" ? " ↑" : " ↓") : ""}
    </th>
  );

  const rowsToShow = reviewMode ? reviewDevices : filtered;
  const tableViewKey = [
    reviewMode ? "review" : "devices",
    campusFilter,
    reviewMode ? reviewYear : yearFilter || "all-years",
    showAll ? "all" : "budget",
    sortCol,
    sortDir,
    search,
  ].join("|");
  const tableMinWidth = reviewMode ? 1260 : 980;

  const statusMenuReview = statusMenu?.serial
    ? (reviewState.devices[statusMenu.serial] || { status: "unreviewed" })
    : null;

  const openStatusMenu = (serial, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setStatusMenu(current => current?.serial === serial ? null : {
      serial,
      top: rect.bottom + 4,
      left: Math.max(8, rect.right - 190),
    });
  };

  return (
    <div className="fade-up" style={{ width: "100%", minWidth: 0 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 800, letterSpacing: "-0.03em", marginBottom: 4 }}>{lifecycleYears}-Year Devices</h1>
        <p style={{ color: "var(--muted)", fontSize: 13 }}>
          {readOnly ? "Archived historical device view. Filtering and CSV export remain available; budget review changes are disabled." : reviewMode ? `Budget review for ${reviewYear}. Verify each device before finalising the budget list.` : "All tracked staff devices. Click column headers to sort."}
        </p>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search serial, type, assigned to…" style={{ flex: 1, minWidth: 200, padding: "8px 14px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 13 }} />
        {!reviewMode ? (
          <>
            <select value={campusFilter} onChange={e => { setCampusFilter(e.target.value); setReviewStartError(""); }} style={{ padding: "8px 14px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 13 }}>
              {campuses.map(c => <option key={c}>{c}</option>)}
            </select>
            <select value={yearFilter} onChange={e => { setYearFilter(e.target.value); setReviewStartError(""); }} style={{ padding: "8px 14px", background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 13 }}>
              <option value="">All years</option>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </>
        ) : (
          <>
            <span style={{ padding: "8px 12px", background: "rgba(79,156,249,0.1)", border: "1px solid rgba(79,156,249,0.25)", borderRadius: 8, color: "var(--accent)", fontSize: 12, fontWeight: 700 }}>
              Campus: {campusFilter}
            </span>
            <span style={{ padding: "8px 12px", background: "rgba(79,156,249,0.1)", border: "1px solid rgba(79,156,249,0.25)", borderRadius: 8, color: "var(--accent)", fontSize: 12, fontWeight: 700 }}>
              Year: {reviewYear}
            </span>
          </>
        )}
        {!reviewMode && (
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)", cursor: "pointer" }}>
            <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} /> Show accessories
          </label>
        )}
        <div style={{ fontSize: 12, color: "var(--muted)" }}>{rowsToShow.length} devices</div>
        {!reviewMode ? (
          <>
            {!readOnly && (
              <button onClick={startBudgetReview} style={{ background: "var(--success)", color: "#07120c", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                Budget Review
              </button>
            )}
            <button onClick={exportCSV} style={{ background: "var(--accent)", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              Export CSV
            </button>
          </>
        ) : (
          <>
            <button onClick={exitReview} style={{ background: "var(--bg2)", color: "var(--muted)", border: "1px solid var(--border)", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              Exit Review
            </button>
            <button onClick={saveCurrentReview} disabled={!reviewLoaded || manualSaving} style={{ background: manualSaving ? "var(--bg3)" : isDirty ? "var(--warn)" : "var(--bg2)", color: isDirty && !manualSaving ? "#07120c" : "var(--text)", border: `1px solid ${isDirty && !manualSaving ? "var(--warn)" : "var(--border)"}`, padding: "8px 18px", borderRadius: 8, cursor: reviewLoaded && !manualSaving ? "pointer" : "default", fontSize: 12, fontWeight: 700, opacity: reviewLoaded ? 1 : 0.6 }}>
              {manualSaving ? "Saving…" : isDirty ? "Save Changes" : "Save Review"}
            </button>
            <button onClick={reloadSharedReview} disabled={!reviewLoaded} style={{ background: "var(--bg2)", color: "var(--muted)", border: "1px solid var(--border)", padding: "8px 18px", borderRadius: 8, cursor: reviewLoaded ? "pointer" : "default", fontSize: 12, fontWeight: 600, opacity: reviewLoaded ? 1 : 0.6 }}>
              Reload Shared Review
            </button>
            <button onClick={() => { setFutureSearch(""); setFuturePickerOpen(true); }} disabled={!reviewLoaded || campusFilter === "All"} style={{ background: "var(--bg2)", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 18px", borderRadius: 8, cursor: reviewLoaded && campusFilter !== "All" ? "pointer" : "default", fontSize: 12, fontWeight: 700, opacity: reviewLoaded && campusFilter !== "All" ? 1 : 0.6 }}>
              Add Future EOL Device
            </button>
            <button onClick={addMiscLineItem} disabled={!reviewLoaded || campusFilter === "All"} style={{ background: "var(--bg2)", color: "var(--text)", border: "1px solid var(--border)", padding: "8px 18px", borderRadius: 8, cursor: reviewLoaded && campusFilter !== "All" ? "pointer" : "default", fontSize: 12, fontWeight: 700, opacity: reviewLoaded && campusFilter !== "All" ? 1 : 0.6 }}>
              Add Misc Item
            </button>
            <button onClick={exportReviewCSV} style={{ background: "var(--accent)", color: "#fff", border: "none", padding: "8px 18px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              Export Review CSV
            </button>
            <button onClick={finalizeReview} disabled={finalizing || !reviewLoaded} style={{ background: reviewSummary.unreviewed === 0 ? "var(--success)" : "var(--bg3)", color: reviewSummary.unreviewed === 0 ? "#07120c" : "var(--muted)", border: "1px solid var(--border)", padding: "8px 18px", borderRadius: 8, cursor: finalizing || !reviewLoaded ? "default" : "pointer", fontSize: 12, fontWeight: 700, opacity: finalizing || !reviewLoaded ? 0.65 : 1 }}>
              {finalizing ? "Finalising…" : `${currentCampusFinalization?.finalizedAt ? "Re-finalise" : "Finalise"} ${reviewYear} Review`}
            </button>
          </>
        )}
      </div>
      {reviewStartError && !reviewMode && (
        <div style={{ marginTop: -8, marginBottom: 16, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", color: "var(--warn)", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 650 }}>
          {reviewStartError}
        </div>
      )}

      {reviewMode && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
          {[
            ["Total", reviewSummary.total, "review scope"],
            ["Verified", reviewSummary.verified, "confirmed valid"],
            ["Needs Jira", reviewSummary.needsJiraUpdate, "included, follow up"],
            ["Added", reviewSummary.addToBudget, "manual additions"],
            ["Misc", reviewSummary.misc, "line items"],
            ["Excluded", reviewSummary.excluded, "removed from budget"],
            ["Unreviewed", reviewSummary.unreviewed, "must be cleared"],
            ["Budget", fmt(reviewSummary.budget), `${reviewSummary.included} included`],
          ].map(([label, value, sub]) => (
            <div key={label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: label === "Unreviewed" && value > 0 ? "var(--warn)" : "var(--text)" }}>{value}</div>
              <div style={{ fontSize: 11, color: "var(--muted)" }}>{sub}</div>
            </div>
          ))}
          {(finalizeError || currentCampusFinalization?.finalizedAt) && (
            <div style={{ gridColumn: "1 / -1", background: finalizeError ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${finalizeError ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`, color: finalizeError ? "var(--danger)" : "var(--success)", borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
              {finalizeError || `Finalised by ${currentCampusFinalization.finalizedBy?.name || "Unknown reviewer"} on ${new Date(currentCampusFinalization.finalizedAt).toLocaleString("en-AU")}${currentCampusFinalization.jiraTickets?.length ? ` · Jira ${currentCampusFinalization.jiraTickets.map(t => `${t.campus}: ${t.key}`).join(" · ")}` : currentCampusFinalization.jiraKey ? ` · Jira ${currentCampusFinalization.jiraKey}` : ""}`}
            </div>
          )}
          {conflictError && (
            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "var(--danger)", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 600 }}>
              <span>{conflictError}</span>
              <button onClick={reloadSharedReview} style={{ background: "var(--danger)", color: "#fff", border: "none", padding: "6px 14px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>Reload Latest</button>
            </div>
          )}
          {reviewSyncStatus && (
            <div style={{ gridColumn: "1 / -1", color: "var(--muted)", fontSize: 11 }}>
              {reviewSyncStatus} · Cost basis {fmt(devicePrice ?? 1704)} per staff device · Signed in as {currentReviewer.name}
            </div>
          )}
          {manualSaveStatus && (
            <div style={{ gridColumn: "1 / -1", background: manualSaveStatus.includes("failed") ? "rgba(239,68,68,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${manualSaveStatus.includes("failed") ? "rgba(239,68,68,0.25)" : "rgba(34,197,94,0.25)"}`, color: manualSaveStatus.includes("failed") ? "var(--danger)" : "var(--success)", borderRadius: 8, padding: "10px 14px", fontSize: 12, fontWeight: 600 }}>
              {manualSaveStatus}
            </div>
          )}
          {search.trim() && (
            <div style={{ gridColumn: "1 / -1", color: "var(--muted)", fontSize: 11 }}>
              Search is filtering the current review scope and any already-added future EOL devices.
            </div>
          )}
        </div>
      )}

      {reviewMode && reviewMiscItems.length > 0 && (
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, fontWeight: 700 }}>Miscellaneous Budget Items</div>
          <div style={{ display: "grid", gap: 8 }}>
            {reviewMiscItems.map(item => (
              <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, padding: "9px 10px" }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ color: "var(--text)", fontSize: 12, fontWeight: 750, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.description}</div>
                  <div style={{ color: "var(--muted)", fontSize: 11 }}>{item.note || "No note"} · added by {item.addedBy?.name || "Unknown reviewer"}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                  <span style={{ color: "var(--warn)", fontSize: 13, fontWeight: 800, fontFamily: "var(--font-mono)" }}>{fmt(item.amount || 0)}</span>
                  <button onClick={() => removeMiscLineItem(item.id)} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", padding: "5px 9px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", maxWidth: "100%" }}>
        <div style={{ overflowX: "auto", maxWidth: "100%", WebkitOverflowScrolling: "touch" }}>
          <table key={tableViewKey} style={{ width: "100%", minWidth: tableMinWidth, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th col="serial" label="Serial" />
                <Th col="jiraDescription" label="Jira Description" />
                <Th col="deviceType" label="Type" />
                <Th col="campus" label="Campus" />
                <Th col="dateOrdered" label="Ordered" />
                <Th col="orderNo" label="Order No" />
                <Th col="eolYear" label="EOL" />
                <Th col="assignedTo" label="Assigned To (Jira)" />
                {hasDefender && <Th col="defenderLastUser" label="Last Login (Azure)" />}
                {hasDefender && <Th col="defenderLastLogon" label="Last Seen" />}
                {hasJira && <Th col="jiraStatus" label="STATUS (JIRA)" />}
                {reviewMode && <th style={{ padding: "10px 14px", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap" }}>Review Status</th>}
                {reviewMode && <th style={{ padding: "10px 14px", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap" }}>Review Notes</th>}
                {reviewMode && <th style={{ padding: "10px 14px", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "left", whiteSpace: "nowrap" }}>Reviewed By</th>}
              </tr>
            </thead>
            <tbody>
              {rowsToShow.slice(0, 300).map((d, i) => {
                const eolYear = Number(d.eolYear);
                const isEolSoon = eolYear === currentYear || eolYear === currentYear + 1;
                const review = reviewState.devices[d.serial] || { status: "unreviewed", note: "" };
                const isSearchMatch = reviewMode && !!search.trim() && matchesSearch(d, search.trim().toLowerCase());
                const isAddedFutureDevice = reviewMode && review.status === ADD_TO_BUDGET && Number(d.eolYear) !== Number(reviewYear);
                const rowBackground = isSearchMatch
                  ? "rgba(245,158,11,0.12)"
                  : i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)";
                return (
                  <tr key={`${tableViewKey}|${d.serial || "row"}|${d.campus || "campus"}|${d.eolYear || "year"}|${i}`} style={{ borderBottom: isSearchMatch ? "1px solid rgba(245,158,11,0.35)" : "1px solid var(--border)", background: rowBackground, opacity: reviewMode && review.status === "excluded" ? 0.58 : 1, boxShadow: isSearchMatch ? "inset 3px 0 0 var(--warn)" : "none" }}>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, color: "var(--accent)" }}>{d.serial}</td>
                    <td
                      style={{ padding: "9px 14px", fontSize: 12, color: "var(--text)", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={d.jiraDescription || undefined}
                    >
                      {d.jiraDescription || <span style={{ color: "var(--muted)" }}>—</span>}
                    </td>
                    <td style={{ padding: "9px 14px", fontSize: 11 }}>
                      <span style={{ background: "rgba(79,156,249,0.1)", color: "var(--accent)", padding: "2px 8px", borderRadius: 8, fontWeight: 500 }}>{d.deviceType}</span>
                    </td>
                    <td style={{ padding: "9px 14px" }}><CampusBadge campus={d.campus} /></td>
                    <td style={{ padding: "9px 14px", fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{d.dateOrdered}</td>
                    <td style={{ padding: "9px 14px", fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>{d.orderNo}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, fontWeight: 700, fontFamily: "var(--font-mono)", color: isEolSoon ? "var(--danger)" : "var(--text)" }}>{d.eolYear}</td>
                    <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--text)" }}>{d.assignedTo || <span style={{color:"var(--muted)"}}>—</span>}</td>
                    {hasDefender && (() => {
                      const differs = d.defenderLastUser && d.assignedTo &&
                        d.defenderLastUser.toLowerCase() !== d.assignedTo.toLowerCase();
                      return (
                        <td title={d.defenderLastDisplayName || undefined} style={{ padding: "9px 14px", fontSize: 12, color: differs ? "var(--warn)" : "var(--text)" }}>
                          {d.defenderLastUser || <span style={{color:"var(--muted)"}}>—</span>}
                        </td>
                      );
                    })()}
                    {hasDefender && (
                      <td style={{ padding: "9px 14px", fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-mono)" }}>
                        {formatDate(d.defenderLastLogon) || <span>—</span>}
                      </td>
                    )}
                    {hasJira && <td style={{ padding: "9px 14px" }}><StatusBadge status={d.jiraStatus} /></td>}
                    {reviewMode && (
                      <td style={{ padding: "9px 14px", position: "relative", whiteSpace: "nowrap" }}>
                        <span style={{ display: "inline-block", minWidth: 108, marginRight: 8, color: review.status === "verified" || review.status === ADD_TO_BUDGET ? "var(--success)" : review.status === "excluded" ? "var(--danger)" : review.status === "needs-jira-update" ? "var(--warn)" : "var(--muted)", fontSize: 11, fontWeight: 700 }}>
                          {isAddedFutureDevice ? "ADDED" : REVIEW_OPTIONS.find(o => o.value === review.status)?.label || "Unreviewed"}
                        </span>
                        {isAddedFutureDevice ? (
                          <button onClick={() => removeAddedBudgetDevice(d.serial)} style={{ marginLeft: 6, background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                            Remove
                          </button>
                        ) : (
                          <button onClick={e => openStatusMenu(d.serial, e)} style={{ background: "var(--bg3)", border: "1px solid var(--border)", color: "var(--text)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                            Change
                          </button>
                        )}
                      </td>
                    )}
                    {reviewMode && (
                      <td style={{ padding: "9px 14px" }}>
                        <input value={review.note || ""} onChange={e => setReview(d.serial, { note: e.target.value })} placeholder="Optional note" style={{ minWidth: 180, padding: "6px 8px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 6, color: "var(--text)", fontSize: 12 }} />
                      </td>
                    )}
                    {reviewMode && (
                      <td title={review.reviewedAt ? new Date(review.reviewedAt).toLocaleString("en-AU") : undefined} style={{ padding: "9px 14px", fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
                        {review.reviewedBy?.name || "—"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rowsToShow.length > 300 && (
            <div style={{ padding: "10px 16px", fontSize: 12, color: "var(--muted)", borderTop: "1px solid var(--border)" }}>
              Showing 300 of {rowsToShow.length} — export CSV for all.
            </div>
          )}
        </div>
      </div>
      {statusMenu && (
        <>
          <button
            aria-label="Close status menu"
            onClick={() => setStatusMenu(null)}
            style={{ position: "fixed", inset: 0, zIndex: 9998, background: "transparent", border: "none", padding: 0, cursor: "default" }}
          />
          <div style={{ position: "fixed", zIndex: 9999, top: statusMenu.top, left: statusMenu.left, minWidth: 210, background: "#111722", border: "1px solid rgba(148,163,184,0.35)", borderRadius: 8, boxShadow: "0 18px 45px rgba(0,0,0,0.65)", padding: 6 }}>
            {REVIEW_OPTIONS.map(o => (
              <button key={o.value} onClick={() => applyStatusChange(statusMenu.serial, o.value)} style={{ display: "block", width: "100%", textAlign: "left", background: o.value === (statusMenuReview?.status || "unreviewed") ? "rgba(79,156,249,0.18)" : "#111722", border: "none", color: "var(--text)", padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 650 }}>
                {o.label}
              </button>
            ))}
          </div>
        </>
      )}
      {futurePickerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10001, background: "rgba(2,6,23,0.72)", backdropFilter: "blur(2px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ width: "min(980px, 100%)", maxHeight: "82vh", background: "#111722", border: "1px solid rgba(148,163,184,0.35)", borderRadius: 12, boxShadow: "0 24px 70px rgba(0,0,0,0.72)", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 850, color: "var(--text)" }}>Add Future EOL Device</div>
                <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>
                  Search future-EOL staff devices at {campusFilter}. Added devices will use the current cost basis and appear in this Budget Review.
                </div>
              </div>
              <button onClick={() => setFuturePickerOpen(false)} style={{ background: "transparent", border: "1px solid var(--border)", color: "var(--muted)", padding: "6px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                Close
              </button>
            </div>
            <div style={{ padding: 14, borderBottom: "1px solid var(--border)" }}>
              <input
                value={futureSearch}
                onChange={e => setFutureSearch(e.target.value)}
                placeholder="Search serial, Jira description, assigned to, order no..."
                autoFocus
                style={{ width: "100%", padding: "9px 12px", background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontSize: 13 }}
              />
            </div>
            <div style={{ overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780 }}>
                <thead>
                  <tr>
                    <Th col="serial" label="Serial" />
                    <Th col="jiraDescription" label="Jira Description" />
                    <Th col="eolYear" label="EOL" />
                    <Th col="assignedTo" label="Assigned To (Jira)" />
                    {hasDefender && <Th col="defenderLastUser" label="Last Login (Azure)" />}
                    <th style={{ padding: "10px 14px", fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600, background: "var(--bg3)", borderBottom: "1px solid var(--border)", textAlign: "right", whiteSpace: "nowrap" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {futureCandidateDevices.slice(0, 80).map(d => {
                    const matchActive = !!futureSearch.trim();
                    return (
                      <tr key={d.serial} style={{ background: matchActive ? "rgba(245,158,11,0.12)" : "transparent", borderBottom: matchActive ? "1px solid rgba(245,158,11,0.35)" : "1px solid var(--border)", boxShadow: matchActive ? "inset 3px 0 0 var(--warn)" : "none" }}>
                        <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--accent)", whiteSpace: "nowrap" }}>{d.serial}</td>
                        <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--text)", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.jiraDescription || d.description}>{d.jiraDescription || d.description || "—"}</td>
                        <td style={{ padding: "9px 14px", fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 800, color: "var(--text)", whiteSpace: "nowrap" }}>{d.eolYear}</td>
                        <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--text)", whiteSpace: "nowrap" }}>{d.assignedTo || <span style={{ color: "var(--muted)" }}>—</span>}</td>
                        {hasDefender && <td style={{ padding: "9px 14px", fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap" }}>{d.defenderLastUser || <span>—</span>}</td>}
                        <td style={{ padding: "9px 14px", textAlign: "right", whiteSpace: "nowrap" }}>
                          <button onClick={() => addFutureBudgetDevice(d.serial)} style={{ background: "var(--success)", color: "#07120c", border: "none", padding: "6px 11px", borderRadius: 7, cursor: "pointer", fontSize: 11, fontWeight: 800 }}>
                            Add To Budget
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {futureCandidateDevices.length === 0 && (
                <div style={{ padding: 34, color: "var(--muted)", fontSize: 13, textAlign: "center" }}>
                  {futureSearch.trim() ? "No matching future-EOL devices found." : "Start typing to narrow the future-EOL device list."}
                </div>
              )}
              {futureCandidateDevices.length > 80 && (
                <div style={{ padding: "10px 14px", color: "var(--muted)", fontSize: 12, borderTop: "1px solid var(--border)" }}>
                  Showing first 80 matches. Keep typing to narrow the list.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {reviewBusyMessage && (
        <div style={{ position: "fixed", inset: 0, zIndex: 10000, background: "rgba(2,6,23,0.46)", backdropFilter: "blur(1px)", display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "auto" }}>
          <div style={{ background: "#111722", border: "1px solid rgba(148,163,184,0.35)", borderRadius: 8, boxShadow: "0 18px 45px rgba(0,0,0,0.65)", padding: "14px 18px", color: "var(--text)", fontSize: 13, fontWeight: 700 }}>
            {reviewBusyMessage}
          </div>
        </div>
      )}
    </div>
  );
}
